import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { batchService, staffService } from '../services/api';
import { useApp } from '../context/AppContext';
import { formatTiming } from '../utils/time';
import TimeInput from '../components/TimeInput';

const todayISO = () => new Date().toISOString().slice(0, 10);

// The only subjects a batch can be created/edited as — a plain text
// field let typos and inconsistent labels ("A1", "a1", "A-1") through,
// which broke matching against these same CEFR levels elsewhere (e.g.
// Materials.js's common-materials-by-subject feature). "Interview prep"
// isn't a CEFR level, but is a real, distinct kind of batch offered
// alongside them (usually external — see the "External" checkbox).
const SUBJECT_OPTIONS = ['A1', 'A2', 'B1', 'B2', 'Interview prep'];

const Batches = () => {
  const { currentUser, notify } = useApp();
  const isAdmin = currentUser.role === 'admin';

  const [batches, setBatches] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [sortBy, setSortBy] = useState('batchNumber');

  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [startDate, setStartDate] = useState(todayISO());
  const [teacherId, setTeacherId] = useState('');
  const [isExternal, setIsExternal] = useState(false);

  const [editingBatchId, setEditingBatchId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', subject: '', startDate: '', isExternal: false });

  const [editingTimingId, setEditingTimingId] = useState(null);
  const [timingForm, setTimingForm] = useState({
    startTime: '',
    endTime: '',
    isFlexible: false,
    startTime2: '',
    endTime2: '',
  });

  const loadBatches = useCallback(
    () => batchService.list().then(setBatches).catch((err) => notify(err.message, 'error')),
    [notify]
  );

  useEffect(() => {
    loadBatches();
    if (isAdmin) {
      staffService.list().then(setTeachers).catch((err) => notify(err.message, 'error'));
    }
  }, [notify, loadBatches, isAdmin]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await batchService.create(name, subject, startDate, teacherId || null, isExternal);
      setName('');
      setSubject('');
      setTeacherId('');
      setIsExternal(false);
      notify('Batch created', 'success');
      loadBatches();
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  const handleAssignTeacher = async (batch, newTeacherId) => {
    try {
      await batchService.update(batch.id, { teacherId: newTeacherId || null });
      notify('Teacher assigned', 'success');
      loadBatches();
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  const startEdit = (batch) => {
    setEditingTimingId(null);
    setEditingBatchId(batch.id);
    setEditForm({ name: batch.name, subject: batch.subject || '', startDate: batch.startDate, isExternal: !!batch.isExternal });
  };

  const cancelEdit = () => setEditingBatchId(null);

  const startTimingEdit = (batch) => {
    setEditingBatchId(null);
    setEditingTimingId(batch.id);
    // TimeInput visually defaults an unset value to 12:00 AM — match that
    // here so the saved value always reflects what's actually on screen.
    // Otherwise, leaving one picker untouched while only changing the
    // other silently saves the untouched one as null, wiping it out.
    setTimingForm({
      startTime: batch.startTime ? batch.startTime.slice(0, 5) : '00:00',
      endTime: batch.endTime ? batch.endTime.slice(0, 5) : '00:00',
      isFlexible: !!batch.isFlexible,
      startTime2: batch.startTime2 ? batch.startTime2.slice(0, 5) : '00:00',
      endTime2: batch.endTime2 ? batch.endTime2.slice(0, 5) : '00:00',
    });
  };

  const cancelTimingEdit = () => setEditingTimingId(null);

  const handleSaveTiming = async (e, batchId) => {
    e.preventDefault();
    try {
      await batchService.setTiming(batchId, {
        startTime: timingForm.startTime || null,
        endTime: timingForm.endTime || null,
        isFlexible: timingForm.isFlexible,
        startTime2: timingForm.isFlexible ? timingForm.startTime2 || null : null,
        endTime2: timingForm.isFlexible ? timingForm.endTime2 || null : null,
      });
      notify('Timing updated', 'success');
      setEditingTimingId(null);
      loadBatches();
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  const handleSaveEdit = async (e, batchId) => {
    e.preventDefault();
    try {
      await batchService.update(batchId, editForm);
      notify('Batch updated', 'success');
      setEditingBatchId(null);
      loadBatches();
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  const handleEnd = async (batch) => {
    const confirmed = window.confirm(
      `Mark "${batch.name}" as ended? Attendance can no longer be marked and no more students can be enrolled.`
    );
    if (!confirmed) return;

    try {
      await batchService.end(batch.id);
      notify('Batch ended', 'success');
      loadBatches();
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  const handleDelete = async (batch) => {
    const confirmed = window.confirm(
      `Delete "${batch.name}"? This also removes its enrollments and attendance history. This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      await batchService.remove(batch.id);
      notify('Batch deleted', 'success');
      loadBatches();
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  // Batch names aren't consistently padded ("01", "Batch 9 Online CBE",
  // "Batch 22 Online CBE") so a plain string sort puts "22" before "9" —
  // pull out the first number in the name and compare numerically
  // instead. Names with no number at all sort last.
  const batchNumber = (name) => {
    const match = name.match(/\d+/);
    return match ? parseInt(match[0], 10) : Infinity;
  };

  // External batches (record-only, no attendance) are secondary to the
  // real ones — always grouped after them regardless of the chosen sort,
  // rather than interleaved by date/number.
  const sortedBatches = [...batches].sort((a, b) => {
    if (!!a.isExternal !== !!b.isExternal) return a.isExternal ? 1 : -1;
    return sortBy === 'startDate' ? b.startDate.localeCompare(a.startDate) : batchNumber(a.name) - batchNumber(b.name);
  });

  return (
    <div className="page">
      <h1>Batches</h1>

      {isAdmin && (
        <div className="card">
          <h2>Create a batch</h2>
          <form className="inline-form" onSubmit={handleCreate}>
            <input placeholder="Batch name" value={name} onChange={(e) => setName(e.target.value)} required />
            <select value={subject} onChange={(e) => setSubject(e.target.value)} required>
              <option value="">Select a subject…</option>
              {SUBJECT_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
            <select value={teacherId} onChange={(e) => setTeacherId(e.target.value)}>
              <option value="">Assign Teacher</option>
              {teachers.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={isExternal}
                onChange={(e) => setIsExternal(e.target.checked)}
              />
              External (record only, no attendance)
            </label>
            <button type="submit" className="btn-primary">Create</button>
          </form>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2>{isAdmin ? 'All batches' : 'Your batches'}</h2>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="batchNumber">Sort: Batch number</option>
            <option value="startDate">Sort: Start date (newest)</option>
          </select>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Batch</th>
                <th>Subject</th>
                <th>Starts</th>
                <th>Timing</th>
                <th>Students</th>
                <th>Teacher</th>
                <th>Language</th>
                <th>Status</th>
                <th className="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedBatches.map((b) => {
                if (editingBatchId === b.id) {
                  return (
                    <tr key={b.id}>
                      <td colSpan={9}>
                        <form className="inline-form" onSubmit={(e) => handleSaveEdit(e, b.id)}>
                          <input
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            placeholder="Batch name"
                            required
                          />
                          <select
                            value={editForm.subject}
                            onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
                            required
                          >
                            <option value="">Select a subject…</option>
                            {SUBJECT_OPTIONS.map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                          <input
                            type="date"
                            value={editForm.startDate}
                            onChange={(e) => setEditForm({ ...editForm, startDate: e.target.value })}
                            required
                          />
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={editForm.isExternal}
                              onChange={(e) => setEditForm({ ...editForm, isExternal: e.target.checked })}
                            />
                            External (record only, no attendance)
                          </label>
                          <button type="submit" className="btn-primary">Save</button>
                          <button type="button" className="btn-link" onClick={cancelEdit}>Cancel</button>
                        </form>
                      </td>
                    </tr>
                  );
                }

                if (editingTimingId === b.id) {
                  return (
                    <tr key={b.id}>
                      <td colSpan={9}>
                        <form className="inline-form" onSubmit={(e) => handleSaveTiming(e, b.id)}>
                          <TimeInput
                            value={timingForm.startTime}
                            onChange={(v) => setTimingForm({ ...timingForm, startTime: v })}
                          />
                          <span className="batch-meta">to</span>
                          <TimeInput
                            value={timingForm.endTime}
                            onChange={(v) => setTimingForm({ ...timingForm, endTime: v })}
                          />
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={timingForm.isFlexible}
                              onChange={(e) => setTimingForm({ ...timingForm, isFlexible: e.target.checked })}
                            />
                            Flexible batch (2 timings/day)
                          </label>
                          {timingForm.isFlexible && (
                            <>
                              <TimeInput
                                value={timingForm.startTime2}
                                onChange={(v) => setTimingForm({ ...timingForm, startTime2: v })}
                              />
                              <span className="batch-meta">to</span>
                              <TimeInput
                                value={timingForm.endTime2}
                                onChange={(v) => setTimingForm({ ...timingForm, endTime2: v })}
                              />
                            </>
                          )}
                          <button type="submit" className="btn-primary">Save</button>
                          <button type="button" className="btn-link" onClick={cancelTimingEdit}>Cancel</button>
                        </form>
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr key={b.id}>
                    <td>
                      {b.isExternal ? (
                        b.name
                      ) : (
                        <Link to={`/attendance?batchId=${b.id}`} className="btn-link">{b.name}</Link>
                      )}
                      {b.isExternal && <span className="badge"> External</span>}
                    </td>
                    <td>{b.subject || '—'}</td>
                    <td>{b.startDate}</td>
                    <td>
                      {formatTiming(b) || '—'}{' '}
                      <button className="btn-link" onClick={() => startTimingEdit(b)}>Set</button>
                    </td>
                    <td>{b.studentCount ?? 0}</td>
                    <td>
                      {isAdmin ? (
                        <select
                          value={b.teacherId || ''}
                          onChange={(e) => handleAssignTeacher(b, e.target.value)}
                        >
                          <option value="">Unassigned</option>
                          {teachers.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}{t.teachingLanguage?.length ? ` (${t.teachingLanguage.join(', ')})` : ''}
                            </option>
                          ))}
                        </select>
                      ) : (
                        b.teacher ? `${b.teacher.name}${b.teacher.teachingLanguage?.length ? ` (${b.teacher.teachingLanguage.join(', ')})` : ''}` : '—'
                      )}
                    </td>
                    <td>{b.teacher?.teachingLanguage?.length ? b.teacher.teachingLanguage.join(', ') : '—'}</td>
                    <td>
                      <span className={`badge ${b.status === 'ended' ? 'badge-ended' : 'badge-active'}`}>
                        {b.status === 'ended' ? 'Ended' : 'Active'}
                      </span>
                    </td>
                    <td>
                      <div className="btn-group actions-group">
                        {b.status !== 'ended' && (
                          <button className="btn-secondary" onClick={() => handleEnd(b)}>End</button>
                        )}
                        {isAdmin && (
                          <>
                            <button className="btn-secondary" onClick={() => startEdit(b)}>Edit</button>
                            <button className="btn-secondary btn-danger" onClick={() => handleDelete(b)}>Delete</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Batches;
